import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Theme,
} from "@mui/material";
import { Field } from "../../utils/formSchema";
import CustomInputButton from "../../components/Input/customInputButton";
import DamsTable from "../../components/Table/CollapsibleTable";
import { TRowItem } from "../../components/Table/TableRow";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { usePagination } from "../../hooks/usePagination";
import { useDmsForm } from "../../hooks/useDmsFrom";
import { ContainedButton, TextButton } from "../../components/Button/AppButton";

import { FilterItemButton } from "../../components/Button/FilterItemButton";
import { AssetCategoryDetail, IPagination } from "../../config/types";
import { SideBarSearchInput } from "../../components/Layout/SideBarSearchInput";
import { formatSnakeCaseToReadable } from "../../utils/common";
import { AssetCategoryApi } from "../../services/assets/assetCategory";

interface InputProps {
  placeholder: string;
  label: string;
  handleChange: (item: Field, isPhysical?: boolean, label?: string) => void;
  value?: string;
  size?: "full" | "short" | "long";
  error?: boolean;
  errorMessage?: string;
  required?: boolean;
}

interface Ifilter {
  name: string;
}
export const AssetCategorySelectInput = ({
  placeholder,
  handleChange,
  label,
  error,
  value,
  size = "short",
  errorMessage = "required",
  required = false,
}: InputProps) => {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [selectName, setSelectName] = useState("");
  const [loading, setLoading] = useState(true);
  const { selectedRowItems, toggleRow } = useMangeTableItems(dataset, false);
  const [querySet, setQuerySet] = useState({ is_deleted: false });
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<any>(AssetCategoryApi.getAssetCategories);

  const { form, updateInputField, updateForm, onFormSubmit, getFormValues } =
    useDmsForm<Ifilter>({
      name: "",
    });

  const populateDataSet = (
    data: AssetCategoryDetail[],
    pagination?: IPagination
  ) => {
    setDataset(data);
    updatePaginationInitState(pagination);
  };
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const res = await AssetCategoryApi.getAssetCategories({
        is_deleted: false,
      });
      if (res.data) {
        populateDataSet(res.data, res.pagination);
        if (value) {
          const __default = (await AssetCategoryApi.getAssetCategory(value))
            .data;
          if (__default) {
            setSelectName(__default.name);
            handleChange(
              { value: __default.id, isValid: true },
              __default.asset_type === "physical",
              __default.name
            );
            toggleRow(__default as TRowItem);
          }
        }
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [open, setOpen] = React.useState(false);
  const toggleModel = () => {
    setOpen(!open);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const resetFilter = <K extends keyof Ifilter>(key: K) => {
    updateForm((p) => {
      const state = {
        ...p,
        [key]: { value: "" },
      };
      setQuerySet((prev) => ({ ...prev, [key]: "" }));
      return state;
    });
  };

  useEffect(() => {
    const { data } = onFormSubmit();
    setQuerySet((p) => ({ ...p, ...data }));

    const filterRequest = async () => {
      setLoading(true);
      const res = await AssetCategoryApi.getAssetCategories({
        is_deleted: false,
        ...data,
      });
      if (res.data) {
        populateDataSet(res.data, res.pagination);
      }
      setLoading(false);
    };
    filterRequest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name.value]);

  const handleUserResponse = (row: TRowItem) => {
    const response = toggleRow(row);
    if (response && response.isSelected) {
      handleChange(
        {
          value: row?.id ?? "",
          isValid: row?.id ? true : false,
        },
        row?.asset_type === "physical",
        row?.name
      );
      setSelectName(row?.name);
      handleClose();
    }
  };
  return (
    <Box>
      <CustomInputButton
        loading={loading}
        label={label}
        placeholder={placeholder}
        value={selectName}
        handleChange={toggleModel}
        error={error}
        errorMessage={errorMessage}
        size={size}
        required={required}
      />
      <Dialog
        maxWidth={"xl"}
        open={open}
        sx={{ height: "100%" }}
        onClose={toggleModel}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          paddingRight={2}
        >
          <DialogTitle fontWeight={600}>{"Asset Categories List"}</DialogTitle>
          {value === "" ? (
            <Box marginTop={2}>
              <TextButton title="Continue" />
            </Box>
          ) : (
            <DialogActions>
              <ContainedButton onClick={handleClose} title="Continue" />
            </DialogActions>
          )}
        </Stack>

        <DialogContent sx={styles.content}>
          <Box sx={styles.filterBox}>
            <Stack direction={"row"} spacing={2}>
              <SideBarSearchInput
                placeholder="Search category name..."
                value={form.name.value}
                handleChange={updateInputField("name")}
              />

              {Object.entries(getFormValues())
                .filter(
                  ([_, value]) =>
                    typeof value === "string" && value.trim() !== ""
                )
                .map(([key, value]) => (
                  <FilterItemButton
                    key={key}
                    label={formatSnakeCaseToReadable(key)}
                    tip={value}
                    height={40}
                    onClick={() => resetFilter(key as keyof Ifilter)}
                  />
                ))}
            </Stack>
          </Box>

          <DamsTable
            isModel
            tableHeaders={columns}
            tableKeys={tkeys}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            data={dataset}
            hasHeaderCheckBox={false}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            useTableCheckbox
            onTableItemSelect={handleUserResponse}
            selectedRowItems={selectedRowItems}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

const styles = {
  filterBox: {
    width: "100%",
    marginBottom: 5,
  },
  content: (theme: Theme) => ({
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    [theme.breakpoints.up("xl")]: {
      width: "70vw",
    },
    width: "90vw",
  }),
  table: {
    width: "100vw",
    height: "70%",
  },
};
const tkeys = ["asset_type", "name", "created_at"];
const columns = ["Asset Category Type", "Asset Category Name", "Date Added"];
const hkeys = ["asset_category_tag_names"];
const hcolumns = ["Category Tags"];
